import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { Box, Button, Container, Fab, IconButton, Stack } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'

import defaultNetworkCover from '@/assets/img/network-cover.png'

import { useIsWebView } from '@/hooks/useSizeBreakpoints'

export type NetworkCoverPhotoProps = Readonly<{
  disabled?: boolean
  disableUpload?: boolean
  onUpload?: (file: File) => void
  url?: string
}>

export default function NetworkCoverPhoto(props: NetworkCoverPhotoProps) {
  const { disabled = false, disableUpload = false, onUpload, url } = props

  const isWebView = useIsWebView()
  const { t } = useTranslation()

  const inputRef = useRef<HTMLInputElement>()

  return (
    <Stack position="relative" justifyContent="center">
      <Container maxWidth="xl" sx={{ position: 'absolute', top: '30px', left: 0 }}>
        <Box sx={{ px: isWebView ? 0 : '25px' }}>
          <Fab color="primary" component={Link} disabled={disabled} to="/networks/joins" size="small">
            <ArrowBackIcon fontSize="small" />
          </Fab>
        </Box>
      </Container>
      <Stack direction="column" flexGrow={1} flexShrink={1} minWidth={0} maxWidth="100%">
        <Box position="relative" width="100%" height="100%">
          <Box>
            <Stack direction="row" alignItems="stretch" justifyContent="center" position="relative">
              <Stack direction="column" flexGrow={1} flexShrink={1} alignItems="center" justifyContent="center" minWidth={0}>
                <Box
                  position="relative"
                  sx={{
                    width: '100%',
                    overflow: 'hidden',
                    borderRadius: '0px 0px max(0px, min(8px, (-100% - 4px + 100vw) * 9999)) max(0px, min(8px, (-100% - 4px + 100vw) * 9999)) / 0px 0px 8px 8px',
                  }}
                >
                  <Box width="100%" height={0} paddingTop="30%" position="relative" overflow="hidden">
                    <Stack
                      direction="column"
                      minWidth={0}
                      minHeight={0}
                      alignItems="stretch"
                      justifyContent="space-between"
                      position="absolute"
                      top={0}
                      bottom={0}
                      left={0}
                      right={0}
                    >
                      <Box sx={{ left: '50%', position: 'absolute', top: '50%', transform: 'translate(-50%, -50%)', width: '100%' }}>
                        <Box width="100%" height={0} paddingTop="30%" position="relative" overflow="hidden">
                          <Stack
                            direction="column"
                            minHeight={0}
                            alignItems="stretch"
                            justifyContent="space-between"
                            position="absolute"
                            top={0}
                            bottom={0}
                            left={0}
                            right={0}
                          >
                            <Box
                              component="img"
                              alt="cover"
                              src={url ?? defaultNetworkCover}
                              width="100%"
                              height="100%"
                              sx={[
                                {
                                  position: 'absolute',
                                  left: 0,
                                  top: 0,
                                  right: 0,
                                  bottom: 0,
                                  objectFit: 'fill',
                                },
                                disabled && {
                                  opacity: 0.5,
                                },
                              ]}
                            />
                          </Stack>
                        </Box>
                      </Box>
                    </Stack>
                  </Box>
                  {!disabled && !disableUpload && (
                    <Box position="absolute" bottom={0} left={0} right={0}>
                      <Box position="relative" px="20px" sx={{ backgroundImage: 'linear-gradient(rgba(0,0,0,0), rgba(0,0,0,0.6))' }}>
                        <Stack direction="row" justifyContent="end" px={1.5} py={2}>
                          <Button
                            onClick={() => {
                              inputRef.current?.click()
                            }}
                            variant="neutral"
                          >
                            <Box
                              component="input"
                              data-testid="upload-cover"
                              ref={inputRef}
                              onChange={async (event) => {
                                const file = event.target.files?.[0]
                                if (file) {
                                  onUpload?.(file)
                                }
                              }}
                              type="file"
                              sx={{ display: 'none' }}
                            />
                            {t('common:button.edit.label')}
                          </Button>
                        </Stack>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Stack>
            </Stack>
          </Box>
        </Box>
      </Stack>
    </Stack>
  )
}
